import { FC, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import useGTAG, { GTAGPrefix } from 'src/hooks/useGTAG';
import useModalStore from 'src/hooks/useModalStore';
import useSearchInputFocusStore from 'src/hooks/useSearchInputFocusStore';
import IconButton, {
  IconPropsType,
  VariantBg
} from 'src/shared-components/design-system/IconButton/IconButton';
import cn from 'classnames';
import { BookingStep } from 'src/page-components/checkout/types';
import dynamic from 'next/dynamic';
import Skeleton from 'react-loading-skeleton';
import { delayWrapper } from 'src/utils/delayWrapper';

const NavigationLink = dynamic(
  () =>
    delayWrapper<typeof NavigationLink>(
      import('src/shared-components/NavigationLink/NavigationLink')
    ),
  {
    loading: () => <Skeleton width={'100%'} height={32} />
  }
);

interface NavigationButtonProps {
  alt: string;
  component: FC<IconPropsType>;
  width: number;
  height: number;
  badge?: string;
  title: {
    id: string;
    defaultMessage: string;
  };
  dataCyName?: string;
  isFullFormat?: boolean;
  darkStyle?: boolean;
  className?: string;
  href: string;
  isHideIcon?: boolean;
  buttonComponent?: ReactNode | HTMLElement | string;
  onClick?: (event: Event) => void;
  gtagPrefix?: GTAGPrefix;
  shallow?: boolean;
}

const NavigationButton: FC<NavigationButtonProps> = ({
  badge = null,
  title,
  dataCyName,
  isFullFormat = false,
  darkStyle = false,
  className,
  href,
  isHideIcon = false,
  buttonComponent,
  onClick,
  component,
  gtagPrefix,
  shallow = false
}) => {
  const { updateModalsConfig } = useModalStore();
  const { trackGtagEvent } = useGTAG();

  const { setSearchInputFocus } = useSearchInputFocusStore();

  const handleNavigationLinkClick = (event) => {
    if (onClick) onClick(event);
    updateModalsConfig('mobile-menu-modal', false);
    setSearchInputFocus(false);

    // Offload analytics from main thread
    setTimeout(() => {
      switch (href) {
        case '/account/profile':
          return () => {
            trackGtagEvent(`${gtagPrefix}_mydata`);
            trackGtagEvent(`${gtagPrefix}_profile`);
          };
        case '/account/bookings':
          return trackGtagEvent(`${GTAGPrefix.menu}_mybookings`);
        case '/account/stores':
          return trackGtagEvent(`${GTAGPrefix.menu}_mypharmacies`);
        case `/checkout/cart?step=${BookingStep.ProductList}`:
        case `/checkout/cart?step=${BookingStep.Cart}`:
          return trackGtagEvent(`${gtagPrefix}_cart`);
        default:
          return null;
      }
    });
  };

  const textColor = darkStyle
    ? 'text-white'
    : 'fs-caption-bold uppercase text-mako py-2.5';

  const content = (
    <>
      <div
        className={cn(
          'left-0 flex items-start',
          isFullFormat ? 'w-7' : 'w-auto'
        )}
      >
        {isHideIcon ? (
          <>{buttonComponent}</>
        ) : (
          <IconButton
            icon={{
              alt: 'cart icon',
              component
            }}
            isBg={false}
            variantBg={darkStyle ? VariantBg.DARK : VariantBg.LIGHT}
          />
        )}
      </div>
      <span
        className={cn('ml-2.5 uppercase', textColor, { hidden: !isFullFormat })}
      >
        <FormattedMessage {...title} />
      </span>
      <span
        className={cn(
          isFullFormat
            ? 'ml-auto'
            : 'absolute translate-x-2/3 -translate-y-2/3',
          !badge && 'hidden'
        )}
      >
        <span className="flex fs-header7-bold text-white justify-center items-center align-center w-3 h-3 rounded-full shadow bg-vermilion from-orange200 via-orange300 to-orange400">
          {badge}
        </span>
      </span>
    </>
  );

  return (
    <NavigationLink
      href={href}
      passClassName={cn(
        'relative h-auto flex flex-row items-center',
        isFullFormat ? 'w-auto' : 'justify-center',
        className
      )}
      data-cy={dataCyName}
      onClick={handleNavigationLinkClick}
      // Prefetch if link point to the cart
      prefetch={href?.includes('/checkout/cart')}
      shallow={shallow}
    >
      {content}
    </NavigationLink>
  );
};

export default NavigationButton;
